import { createResource, createSignal, For, Show, createEffect } from "solid-js";
import { useAdminContext } from "./AdminContext";
import socket from "./socket";

export default function AdminNotifications() {
  const [adminStore, setAdminStore] = useAdminContext();
  const [selectedNotification, setSelectedNotification] = createSignal(null);
  const [filters, setFilters] = createSignal({
    search: "",
    status: "",
    email: ""
  });
  const [currentPage, setCurrentPage] = createSignal(1);
  const [message, setMessage] = createSignal(null);

  // Fetch notifications with current filters and pagination
  const [notificationsData, { refetch }] = createResource(() => {
    return new Promise((resolve) => {
      socket.emit(
        "admin-get-notifications", 
        filters(), 
        currentPage(), 
        20, 
        (result) => {
          resolve(result);
        }
      );
    });
  });

  // Reset to first page when filters change
  createEffect(() => {
    // This will trigger when filters change
    const currentFilters = filters();
    setCurrentPage(1);
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "sent":
        return "bg-success";
      case "pending":
        return "bg-warning";
      case "failed":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    refetch();
  };

  // Handle filter reset
  const handleFilterReset = () => {
    setFilters({
      search: "",
      status: "",
      email: ""
    });
    refetch();
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch();
  };

  return (
    <div class="container-fluid mt-4">
      <h1>Email Notification Log</h1>

      <div class="row">
        <div class="col-md-12">
          <Show when={message()}>
            <div class={`alert alert-${message().type === "success" ? "success" : "danger"} mt-3`}>
              {message().text}
            </div>
          </Show>
        </div>
      </div>

      {/* Filter form */}
      <div class="card mb-4">
        <div class="card-header">
          <h5>Filter Notifications</h5>
        </div>
        <div class="card-body">
          <form onSubmit={handleFilterSubmit}>
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Search Terms</label>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search subject, body, or recipient"
                  value={filters().search}
                  onInput={(e) => setFilters({...filters(), search: e.target.value})}
                />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Status</label>
                <select
                  class="form-select"
                  value={filters().status}
                  onChange={(e) => setFilters({...filters(), status: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="sent">Sent</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Recipient Email</label>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Filter by email"
                  value={filters().email}
                  onInput={(e) => setFilters({...filters(), email: e.target.value})}
                />
              </div>
              <div class="col-md-2 d-flex align-items-end mb-3">
                <button type="submit" class="btn btn-primary me-2">Apply</button>
                <button type="button" class="btn btn-secondary" onClick={handleFilterReset}>Reset</button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-5">
          <div class="card">
            <div class="card-header">
              <h5>Notification History</h5>
            </div>
            <div class="card-body">
              <Show when={notificationsData.loading}>
                <p class="text-center">Loading notifications...</p>
              </Show>

              <Show when={notificationsData.error}>
                <p class="text-center text-danger">Error loading notifications</p>
              </Show>

              <Show when={!notificationsData.loading && !notificationsData.error}>
                <div class="list-group notification-list">
                  <Show when={notificationsData()?.notifications?.length === 0}>
                    <p class="text-center">No notifications found matching your criteria.</p>
                  </Show>
                  <For each={notificationsData()?.notifications || []}>
                    {(notification) => (
                      <button
                        class={`list-group-item list-group-item-action ${selectedNotification() && selectedNotification().id === notification.id ? 'active' : ''}`}
                        onClick={() => setSelectedNotification(notification)}
                      >
                        <div class="d-flex w-100 justify-content-between">
                          <h6 class="mb-1 text-truncate" style="max-width: 300px;">{notification.subject}</h6>
                          <span class={`badge ${getStatusBadgeClass(notification.status)}`}>
                            {notification.status}
                          </span>
                        </div>
                        <div class="d-flex w-100 justify-content-between">
                          <small>To: {notification.email || notification.username}</small>
                          <small>{formatDate(notification.created_at)}</small>
                        </div>
                      </button>
                    )}
                  </For>
                </div>

                {/* Pagination */}
                <Show when={notificationsData()?.pagination?.totalPages > 1}>
                  <nav class="mt-3">
                    <ul class="pagination justify-content-center">
                      <li class={`page-item ${currentPage() === 1 ? 'disabled' : ''}`}>
                        <button 
                          class="page-link" 
                          onClick={() => handlePageChange(currentPage() - 1)}
                          disabled={currentPage() === 1}
                        >
                          Previous
                        </button>
                      </li>
                      
                      <For each={Array.from({ length: notificationsData().pagination.totalPages }, (_, i) => i + 1)}>
                        {(page) => (
                          <li class={`page-item ${currentPage() === page ? 'active' : ''}`}>
                            <button 
                              class="page-link" 
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        )}
                      </For>
                      
                      <li class={`page-item ${currentPage() === notificationsData().pagination.totalPages ? 'disabled' : ''}`}>
                        <button 
                          class="page-link" 
                          onClick={() => handlePageChange(currentPage() + 1)}
                          disabled={currentPage() === notificationsData().pagination.totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </Show>
              </Show>
            </div>
          </div>
        </div>

        <div class="col-lg-7">
          <Show when={selectedNotification()}>
            <div class="card">
              <div class="card-header">
                <h5>Notification Details</h5>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <h5>Template</h5>
                  <p>{selectedNotification().template_name}</p>
                </div>
                
                <div class="mb-3">
                  <h5>Recipient</h5>
                  <p>
                    <strong>Username:</strong> {selectedNotification().username}<br />
                    <strong>Name:</strong> {selectedNotification().fullname || 'N/A'}<br />
                    <strong>Email:</strong> {selectedNotification().email || 'N/A'}
                  </p>
                </div>

                <div class="mb-3">
                  <h5>Status</h5>
                  <p>
                    <span class={`badge ${getStatusBadgeClass(selectedNotification().status)}`}>
                      {selectedNotification().status}
                    </span>
                  </p>
                </div>

                <div class="mb-3">
                  <h5>Timing</h5>
                  <p>
                    <strong>Created:</strong> {formatDate(selectedNotification().created_at)}<br />
                    <strong>Sent:</strong> {formatDate(selectedNotification().sent_at)}
                  </p>
                </div>

                <div class="mb-3">
                  <h5>Subject</h5>
                  <p>{selectedNotification().subject}</p>
                </div>

                <div class="mb-3">
                  <h5>Body</h5>
                  <div class="border rounded p-3 bg-light">
                    <pre style="white-space: pre-wrap; font-family: var(--bs-font-sans-serif);">
                      {selectedNotification().body}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </Show>

          <Show when={!selectedNotification()}>
            <div class="card">
              <div class="card-body">
                <p class="text-center text-muted">Select a notification to view details</p>
              </div>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}